import { useTranslation } from 'react-i18next'

import { FormInputsType } from '@pages/FormCreateOrEdit/types'

import {
  GENERATOR_INPUT_TYPE,
  LABEL_ALIGNMENTS,
  LABEL_BIND_TYPES,
  LABEL_POSITIONS,
  POINT_COLOR_BIND_TYPES,
} from '@constants'
import { AutocompleteOption } from '@types'

type Props = {
  objectsOptions: AutocompleteOption[]
  selectedObjectFieldsOptions: AutocompleteOption[]
  selectedObjectCommandsOptions: AutocompleteOption[]
  handleChangeGetDataCommand: (option: AutocompleteOption) => void
  handleChangePointColorBindType: (option: AutocompleteOption) => void
  watchObjectCode: AutocompleteOption<string> | null
}

export const useInputs = (props: Props) => {
  const {
    objectsOptions,
    selectedObjectFieldsOptions,
    selectedObjectCommandsOptions,
    handleChangeGetDataCommand,
    handleChangePointColorBindType,
    watchObjectCode,
  } = props

  const { t } = useTranslation()

  const generalInputs: FormInputsType[] = [
    {
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      name: 'id',
      label: t('label.code'),
      placeholder: t('label.code'),
      rules: { required: true },
    },
    {
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      name: 'title',
      label: t('addPointGroupDialog.title'),
      placeholder: t('addPointGroupDialog.title'),
      rules: { required: true },
    },
    {
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      name: 'objectCode',
      label: t('addPointGroupDialog.objectCode'),
      placeholder: t('addPointGroupDialog.objectCodePlaceholder'),
      autocompleteOptions: objectsOptions || [],
      rules: { required: true },
    },
    ...(watchObjectCode?.id
      ? [
          {
            inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
            name: 'coordinateFields.latitudeField',
            autocompleteOptions: selectedObjectFieldsOptions,
            label: t('addPointGroupDialog.latitudeField'),
            placeholder: t('addPointGroupDialog.latitudeFieldPlaceholder'),
            rules: { required: true },
          },
          {
            inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
            autocompleteOptions: selectedObjectFieldsOptions,
            name: 'coordinateFields.longitudeField',
            label: t('addPointGroupDialog.longitudeField'),
            placeholder: t('addPointGroupDialog.longitudeFieldPlaceholder'),
            rules: { required: true },
          },
        ]
      : []),
  ]

  const labelBindTypeInput: FormInputsType[] = [
    {
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      name: 'label.bindType',
      label: t('addPointGroupDialog.labelBindType'),
      placeholder: t('addPointGroupDialog.labelBindTypePlaceholder'),
      autocompleteOptions: LABEL_BIND_TYPES,
      disabled: true,
      rules: { required: true },
    },
  ]

  const labelInputs: FormInputsType[] = [
    {
      name: 'position',
      inputs: [
        {
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          name: 'label.position',
          label: t('addPointGroupDialog.labelPosition'),
          placeholder: t('addPointGroupDialog.labelPositionPlaceholder'),
          autocompleteOptions: LABEL_POSITIONS,
          rules: { required: true },
          disableClearable: true,
          disabled: true,
        },
        {
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          name: 'label.align',
          label: t('addPointGroupDialog.labelAlign'),
          placeholder: t('addPointGroupDialog.labelAlignPlaceholder'),
          autocompleteOptions: LABEL_ALIGNMENTS,
          rules: { required: true },
          disableClearable: true,
          disabled: true,
        },
      ],
    },
  ]

  const getCommandInputs: FormInputsType[] = [
    {
      name: 'getDataCommand.name',
      autocompleteOptions: selectedObjectCommandsOptions,
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      rules: {
        required: true,
      },
      label: t('timeLinesCreateOrEdit.objectY.command'),
      placeholder: t('timeLinesCreateOrEdit.objectY.command'),
      onChangeAutocomplete: handleChangeGetDataCommand,
    },
  ]

  const pointColorInputs: FormInputsType[] = [
    {
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      name: 'pointStyle.color.bindType',
      label: t('addPointGroupDialog.labelBindType'),
      placeholder: t('addPointGroupDialog.labelBindTypePlaceholder'),
      autocompleteOptions: POINT_COLOR_BIND_TYPES,
      rules: { required: true },
      disableClearable: true,
      onChangeAutocomplete: handleChangePointColorBindType,
    },
  ]

  return {
    generalInputs,
    labelInputs,
    labelBindTypeInput,
    getCommandInputs,
    pointColorInputs,
  }
}
